import {useEffect, useRef} from "react";
import {getDateFormat, getDateLong} from "../../../../../Apis/functions";
import SockJS from "sockjs-client";
import {guardURL} from "../../../../../Apis/Constants";
import {Client} from "@stomp/stompjs";
import {DEFAULT_ZONE_PATH} from "../Model/Model";
import {GetAllZoneTree} from "../../../../../reduxToolkit/EmployeeContractors/EmployeeContractorsApi";
import {useDispatch} from "react-redux";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 */
export const UseWebSocket = ({
                                 item,
                                 isOnline,
                                 setIsOnline,
                                 setPendingToComplete,
                                 setPendingValidation,
                                 setReadyToEnter,
                                 setCheckIn
                             }) => {

    const clientRef = useRef(null);
    const subscriptionsRef = useRef([]);
    const dispatch = useDispatch();
    /**
     * Function to connect to WebSocket
     */
    const connectWebSocket = () => {
        if (!item?.date) {
            setIsOnline(false);
            return;
        }
        const invitationDate =  getDateLong(item?.date);
        const topicDate = getDateFormat(item?.date, false, "YYYY-MM-DD");

        const socket = () => new SockJS(guardURL);
        const client = new Client({
            webSocketFactory: socket,
            debug: () => {},
            reconnectDelay: 5000,
            onConnect: () => {
                setIsOnline(true);
                subscriptionsRef.current.forEach(sub => sub.unsubscribe());
                subscriptionsRef.current = [];

                setPendingToComplete([]);
                setPendingValidation([]);
                setReadyToEnter([]);
                setCheckIn([]);

                const save = client.subscribe(`/topic/incoming_invitation/${DEFAULT_ZONE_PATH}/${topicDate}`, message => {
                    const invitations = JSON.parse(message.body)?.data;
                    if (invitations) {
                        dispatch(GetAllZoneTree()).then(({ payload: { data: { data }}}) => {
                            handleSetInvitations(invitations, data);
                        });
                    }
                });
                subscriptionsRef.current = [save];
                const dto = {invitationDate, zoneId: null };
                client.publish({
                    destination: `/app/incoming-invitation`,
                    body: JSON.stringify(dto)
                });
            },
            onStompError: () => {
                setIsOnline(false);
            },
            onWebSocketClose: () => {
                setIsOnline(false);
            },
            onWebSocketError: () => {
                setIsOnline(false);
            },
        });
        client.activate();
        clientRef.current = client;
    };

    /**
     * Connect to WebSocket
     */
    useEffect(() => {
        const date = item?.date;

        setPendingToComplete([]);
        setPendingValidation([]);
        setReadyToEnter([]);
        setCheckIn([]);

        if (date) {
            connectWebSocket(date);
        }
        return () => {
            subscriptionsRef.current.forEach(sub => sub.unsubscribe());
            subscriptionsRef.current = [];
            if (clientRef.current) {
                clientRef.current.deactivate();
                clientRef.current = null;
            }
        };
    }, [item]);

    /**
     * function to handle and set the values returned from the WebSocket
     * @param {Object} data - Data object received from the WebSocket containing invitation lists.
     * @param {Array} treeZones - Full tree structure of zones used to build breadcrumbs.
     */
    const handleSetInvitations = (data, treeZones) => {
        const zoneId = item?.zone?.id;

        const filterByZone = (list = []) => {
            const aux = list.map(e => ({...e, breadcrumb:  getBreadcrumbById(treeZones, e?.zoneId)}));
            if (!zoneId){
                return aux;
            }
            return aux.filter(inv => inv?.breadcrumb?.map(e => e.id)?.includes(zoneId));
        };

        setPendingToComplete(filterByZone(data?.pendingToComplete));
        setPendingValidation(filterByZone(data?.pendingValidation));
        setReadyToEnter(filterByZone(data?.readyToEnter));
        setCheckIn(filterByZone(data?.checkIn));
    };

    /**
     * Retrieves the breadcrumb path for a node within a hierarchical tree,
     * starting from the child node up to the root parent.
     *
     * The function recursively traverses the tree searching for the node
     * with the given `targetId` and builds the full path using `id` and `name`.
     *
     * @param {Array<Object>} tree - Root-level tree nodes.
     * @param {string} targetId - ID of the target node.
     * @returns {Array<{id: string, name: string}>}
     * Returns an array ordered from child to parent.
     * If the node is not found, an empty array is returned.
     *
     * @example
     * const breadcrumb = getBreadcrumbById(tree, '80beea59-638f');
     * // Result:
     * // [
     * //   { id: '80beea59-638f', name: 'Area 1' },
     * //   { id: 'c60148f2-1967', name: 'Building A' },
     * //   { id: '71f79065-f2c9', name: 'Juriquilla' },
     * //   { id: 'f4579c7a-be40', name: 'Queretaro' }
     * // ]
     */
    function getBreadcrumbById(tree, targetId) {
        function traverse(nodes, path) {
            for (const node of nodes) {
                const newPath = [...path, { id: node.id, name: node.name }];

                if (node.id === targetId) {
                    return newPath;
                }

                if (node.children && node.children.length > 0) {
                    const result = traverse(node.children, newPath);
                    if (result) {
                        return result;
                    }
                }
            }
            return null;
        }

        return traverse(tree, []) || [];
    }


    /**
     * Function to handle WebSocket reconnection on failure
     */
    const reconnectWebSocket = () => {
        if (!isOnline && !clientRef.current?.connected) {
            connectWebSocket();
        }
    };

    return {reconnectWebSocket};
};
